import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Col, Row, Table } from 'reactstrap';
import InfoBox from '../../shared/components/custom/InfoBox';
import FormCard from '../../shared/components/custom/FormCard';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Loading from '../../shared/components/custom/Loading';
import useModal from '../../shared/hooks/useModal';
import Api from '../../util/api';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import { partial } from '../../util/translation/translation';
import Pagination from '../../shared/components/pagination/Pagination';
import { formatDate } from '../../util/date-and-time';
import { getReadableFormat, getReadableSize } from './spotlightUtils';
import moment from 'moment';

const SpotlightGroupListPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [groups, setGroups] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const p = partial('HighlightBannerPage');
  const s = partial('shared');
  const getSpotlightGroups = async (
    currentPage = 1,
    pageSize = 10,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    }
  ) => {
    setLoading(true);
    try {
      const resSpotlight = await Api.spotlight.getGroups(
        { page: currentPage, take: pageSize },
        {
          orderBy: orderObject.orderBy,
          direction: orderObject.desc ? 'DESC' : 'ASC',
        },
        searchText
      );
      setGroups(resSpotlight.data);
      setLoading(false);
      setPagination((old) => ({ ...old, total: resSpotlight.count }));
    } catch (err) {
      toast.error(err || p('errorFetchingSlider'));
      setLoading(false);
    }
    setHasFetched(true);
  };
  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      getSpotlightGroups(pager.currentPage, pager.pageSize, order);
    }
  };
  const [handleModal, visible, deleteId] = useModal();
  useEffect(() => {
    getSpotlightGroups();
  }, []);

  const renderHighligts = () => {
    return groups.map((group, i) => {
      const activeTileCount = group.tiles.filter((tile) =>
        moment().isBetween(
          moment(tile.publicationDate),
          moment(tile.expirationDate)
        )
      ).length;

      return (
        <tr
          style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
          key={`group-${group.id}`}
        >
          <td>
            <div className="flex-Vcenter">
              <Link to={`spotlight/group/edit/${group.id}?tab=2`}>
                {group.title}
              </Link>
            </div>
          </td>
          <td>
            <div>{getReadableFormat(group.format)}</div>
            <small className="text-muted">{getReadableSize(group.size)}</small>
          </td>
          <td>{activeTileCount}</td>
          <td>
            <span
              className={`status-dot ${
                group.descriptionAvailable
                  ? 'status-dot--on'
                  : 'status-dot--off'
              }`}
            >
              {group.descriptionAvailable ? <CheckIcon /> : <CloseIcon />}
            </span>
          </td>
          <td style={{ textTransform: 'capitalize' }}>
            {formatDate(group.createdAt)}
          </td>
          <td>
            <div className="icon-actions">
              <SquareIconButton
                icon={<EditOutlineIcon />}
                onClick={() =>
                  history.push(`/spotlight/group/edit/${group.id}?tab=2`)
                }
                title={s('edit')}
                id={`edit-spotlight-${group.id}`}
              />
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(group.id)}
                title={s('delete')}
                tone="danger"
                id={`delete-spotlight-${group.id}`}
              />
            </div>
          </td>
        </tr>
      );
    });
  };

  const deleteHighlightEntity = async () => {
    setLoading(true);
    try {
      await Api.spotlight.deleteGroup(deleteId);
      handleModal();
      toast.success(p('sliderDeleted'));
      getSpotlightGroups(pagination.currentPage, pagination.perPage, order);
    } catch (err) {
      setLoading(false);
      toast.error(err || s('somethingWentWrong'));
    }
  };
  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    getSpotlightGroups(1, 10, newOrder);
  };
  const handleSearch = (key) => {
    if (key === 'Enter') {
      getSpotlightGroups(1, 10, order);
    }
    return null;
  };
  return (
    <>
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={s('spotlight')}
        modalAction={deleteHighlightEntity}
      />
      <Row>
        <Col>
          <InfoBox>
            <strong>{p('appLayoutHintTitle')}</strong> {p('appLayoutHintText')}{' '}
            <Link to="/app_layout">{p('openAppLayout')}</Link>
          </InfoBox>
        </Col>
      </Row>
      <Row>
        <Col>
          <Loading loading={loading} />
          <FormCard
            title={p('spotlight')}
            actions={
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <div className="form noWrap">
                  <div className="form__form-group products-list__search">
                    <input
                      placeholder={s('search')}
                      value={searchText}
                      onKeyPress={(event) => handleSearch(event.key)}
                      onChange={(event) => setSearchText(event.target.value)}
                      name="search"
                    />
                    <MagnifyIcon />
                  </div>
                </div>
                <Link to="/spotlight/group/create">
                  <Button size="sm" color="primary" className="mb-0">
                    {s('create')}
                  </Button>
                </Link>
              </div>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            <Table responsive striped className="table--roomy">
              <thead>
                <tr style={{ color: '#4D576A' }}>
                  <th className="sortable" onClick={() => handleSort('title')}>
                    <div className="flex alignItems-center">
                      {s('title')} {renderSort('title')}
                    </div>
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('format')} / {p('size')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('activeTiles')}
                  </th>
                  <th className="bold" style={{ color: '#4D576A' }}>
                    {p('description')}
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex alignItems-center">
                      {s('publicationDate')} {renderSort('createdAt')}
                    </div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {hasFetched && groups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      {p('noSpotlights')}
                    </td>
                  </tr>
                ) : (
                  renderHighligts()
                )}
              </tbody>
            </Table>
          </FormCard>
          <Pagination
            items={pagination.total}
            onChangePage={onChangePage}
            initialPage={pagination.currentPage}
          />
        </Col>
      </Row>
    </>
  );
};

export default SpotlightGroupListPage;
